import React, { useEffect, useState, Key, useRef } from 'react';
import { Table, Card, message, Button, Input, DatePicker, Select, Space, Dropdown, Radio, Tooltip, Modal, type MenuProps } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, PrinterOutlined, EyeOutlined, StopOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import type { ColumnsType } from 'antd/es/table';
import locale from 'antd/es/date-picker/locale/es_ES';
import axios from 'axios';
import { theme } from '../../theme';

const { RangePicker } = DatePicker;
const { confirm } = Modal;
const { Option } = Select;

interface LiquidacionFlete {
  id: number;
  numero_liquidacion: number;
  fecha_liquidacion: string;
  fecha_comprobante: string;
  transportista: number;
  transportista_nombre: string;
  tipo_comprobante: string;
  letra_comprobante: string;
  numero_comprobante: string;
  subtotal: number;
  iva: number;
  tasa_iva: number;
  otros_impuestos: number;
  alta: number;
  detalles: Array<{
    id: number;
    balanza: {
      id: number;
      numero_ticket: number;
      fecha_ticket: string;
      cliente_nombre: string;
      productor_nombre: string;
      peso_neto: number;
    };
    subtotal: string;
  }>;
}

interface Transportista {
  id: number;
  nc: string;
  denominacion: string;
}

interface ApiResponse {
  status: string;
  data: LiquidacionFlete[];
}

interface AnulacionResponse {
  status: string;
  message?: string;
}

const FletesLiquidaciones: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  const containerRef = useRef<HTMLDivElement>(null);
  const [liquidaciones, setLiquidaciones] = useState<LiquidacionFlete[]>([]);
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [selectedTransportista, setSelectedTransportista] = useState<Transportista | null>(null);
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTransportistas, setLoadingTransportistas] = useState(false);
  const [selectedRow, setSelectedRow] = useState<LiquidacionFlete | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const formatNumber = (value: number) => {
    const numericValue = Number(value) || 0;
    return numericValue.toLocaleString('es-AR', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateTotal = (record: LiquidacionFlete) => {
    const subtotal = Number(record.subtotal) || 0;
    const iva = Number(record.iva) || 0;
    const otros = Number(record.otros_impuestos) || 0;
    return subtotal + iva + otros;
  };

  // Cargar transportistas al montar el componente
  const fetchTransportistas = async () => {
    try {
      setLoadingTransportistas(true);
      const token = await getAccessTokenSilently();

      if (!id_empresa) {
        message.error('Error: Datos de empresa no disponibles');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Respuesta del backend:', response.data);
      if (response.data) {
        setTransportistas(response.data);
        console.log('Transportistas cargados:', response.data);
      } else {
        setTransportistas([]);
        message.warning('No se encontraron transportistas');
      }
    } catch (error) {
      console.error('Error al obtener transportistas:', error);
      message.error('Error al cargar los transportistas');
      setTransportistas([]);
    } finally {
      setLoadingTransportistas(false);
    }
  };

  const fetchLiquidaciones = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      if (!id_empresa || !id_sucursal) {
        message.error('Error: Datos de empresa o sucursal no disponibles');
        return;
      }

      // Construir la URL con filtro de transportista si está seleccionado
      let url = `${process.env.REACT_APP_BALANZA_URL}/listar_liquidacion_fletes/${id_empresa}/${id_sucursal}/`;
      
      // Si hay un transportista seleccionado, agregar el filtro
      if (selectedTransportista) {
        url += `?transportista=${selectedTransportista.id}`;
        console.log('Buscando liquidaciones para transportista:', selectedTransportista.id, selectedTransportista.denominacion);
      } else {
        console.log('Buscando todas las liquidaciones (sin filtro de transportista)');
      }

      console.log('URL de búsqueda:', url);

      const response = await axios.get<ApiResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Respuesta del backend:', response.data);

      if (response.data && response.data.data) {
        setLiquidaciones(response.data.data);
        console.log('Liquidaciones encontradas:', response.data.data.length);
      } else {
        setLiquidaciones([]);
        message.warning('No se encontraron liquidaciones');
        console.log('No se encontraron liquidaciones');
      }
    } catch (error) {
      console.error('Error al obtener liquidaciones:', error);
      message.error('Error al cargar las liquidaciones');
      setLiquidaciones([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar transportistas al montar el componente
  useEffect(() => {
    fetchTransportistas();
  }, [getAccessTokenSilently]);

  // Log para verificar el estado de transportistas
  useEffect(() => {
    console.log('Estado actual de transportistas:', transportistas);
  }, [transportistas]);

  // Cerrar dropdown cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.transportista-search-container')) {
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTransportistaChange = (transportista: Transportista | null) => {
    console.log('Transportista seleccionado:', transportista);
    setSelectedTransportista(transportista);
    setShowSearchTransportista(false);
  };

  const handleSearch = () => {
    fetchLiquidaciones();
  };

  const handleNewClick = () => {
    // Agregar clase de transición para slide out
    if (containerRef.current) {
      containerRef.current.classList.add('slide-out-left');
      // Navegar después de que comience la animación
      setTimeout(() => {
        navigate('/listar_pendientes_fletes');
      }, 300); // Duración de la animación (300ms según especificaciones de Ant Design)
    } else {
      navigate('/listar_pendientes_fletes');
    }
  };

  const handleActionClick = (record: LiquidacionFlete) => {
    setSelectedRow(record);
  };

  const handlePrint = () => {
    if (selectedRow) {
      navigate(`/imprimir_liquidacion_fletes/${selectedRow.id}`);
    }
  };

  const handleCancel = () => {
    if (selectedRow) {
      confirm({
        title: 'Confirmar Anulación',
        icon: <ExclamationCircleFilled />,
        content: `¿Está seguro que desea anular la Liquidación de Fletes N° ${selectedRow.numero_liquidacion}?`,
        okText: 'ACEPTAR',
        cancelText: 'CANCELAR',
        okButtonProps: {
          danger: true,
        },
        onOk: async () => {
          try {
            const token = await getAccessTokenSilently();

            if (!id_empresa || !id_sucursal) {
              message.error('Error: Datos de empresa o sucursal no disponibles');
              return;
            }

            const response = await axios.get<AnulacionResponse>(
              `${process.env.REACT_APP_BALANZA_URL}/anular_liquidacion_fletes/${id_empresa}/${id_sucursal}/${selectedRow.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (response.data.status === 'success') {
              message.success(`Liquidación N° ${selectedRow.numero_liquidacion} anulada correctamente`);
              setRefreshKey(oldKey => oldKey + 1);
              setSelectedRow(null);
            } else {
              message.error(response.data.message || 'Error al anular la liquidación');
            }
          } catch (error: any) {
            console.error('Error al anular liquidación:', error);
            message.error(error.response?.data?.message || 'Error al anular la liquidación');
          }
        },
        onCancel() {
          console.log('Cancelado');
        },
      });
    }
  };

  const columns: ColumnsType<LiquidacionFlete> = [
    {
      title: 'Opciones',
      key: 'action',
      width: 80,
      fixed: 'left',
      render: (_, record) => (
        <Radio 
          checked={selectedRow?.id === record.id}
          onClick={() => handleActionClick(record)}
          disabled={record.alta === 0}
        />
      ),
    },
    {
      title: 'N° Liquidación',
      dataIndex: 'numero_liquidacion',
      key: 'numero_liquidacion',
      width: 120,
      align: 'center',
      render: (text, record) => {
        const detallesContent = record.detalles?.map(detalle => {
          const subtotalCalculado = (detalle.balanza.peso_neto * Number(detalle.subtotal)) / 1000;
          return (
            `Ticket: ${detalle.balanza.numero_ticket}
             Fecha: ${new Date(detalle.balanza.fecha_ticket).toLocaleDateString()}
             Cliente: ${detalle.balanza.cliente_nombre}
             Productor: ${detalle.balanza.productor_nombre}
             Peso Neto: ${formatNumber(detalle.balanza.peso_neto)}
             Tarifa: ${formatNumber(Number(detalle.subtotal))}
             Subtotal: ${formatNumber(subtotalCalculado)}`
          );
        }).join('\n\n----------------------------------------\n\n');

        return (
          <Tooltip 
            title={
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                minWidth: '400px',
                maxWidth: '600px',
                padding: '10px',
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
                  Detalles de la Liquidación
                </div>
                {detallesContent}
              </div>
            }
            placement="right"
            color="#fff"
          >
            <span>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_liquidacion',
      key: 'fecha_liquidacion',
      width: 100,
      align: 'center',
      sorter: (a, b) => new Date(a.fecha_liquidacion).getTime() - new Date(b.fecha_liquidacion).getTime(),
      render: (text) => new Date(text).toLocaleDateString(),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const dates = selectedKeys[0] ? String(selectedKeys[0]).split(',') : ['', ''];
        return (
          <div style={{ padding: 8 }}>
            <RangePicker
              locale={locale}
              style={{ marginBottom: 8, width: 250 }}
              onChange={(_, dateStrings) => {
                setSelectedKeys(dateStrings[0] ? [dateStrings.join(',')] : []);
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Filtrar
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Resetear
              </Button>
            </div>
          </div>
        );
      },
      onFilter: (value: boolean | Key, record) => {
        if (!value || typeof value !== 'string') return true;
        const [startDate, endDate] = value.split(',');
        if (!startDate || !endDate) return true;
        
        const recordDate = new Date(record.fecha_liquidacion);
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        return recordDate >= start && recordDate <= end;
      },
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? theme.colors.primary : undefined }} />
      ),
    },
    {
      title: 'Transportista',
      dataIndex: 'transportista_nombre',
      key: 'transportista',
      width: 200,
      ellipsis: true,
      sorter: (a, b) => a.transportista_nombre.localeCompare(b.transportista_nombre),
      filters: Array.from(new Set(liquidaciones.map(l => l.transportista_nombre)))
        .map(transportista => ({ text: transportista, value: transportista })),
      onFilter: (value: boolean | Key, record) => {
        if (typeof value === 'string') {
          return record.transportista_nombre === value;
        }
        return true;
      },
      filterSearch: true,
    },
    {
      title: 'Tipo Comp.',
      dataIndex: 'tipo_comprobante',
      key: 'tipo_comprobante',
      width: 100,
      align: 'center',
    },
    {
      title: 'Número Comp.',
      dataIndex: 'numero_comprobante',
      key: 'numero_comprobante',
      width: 150,
      align: 'center',
      render: (text, record) => `${text}`,
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right',
      render: (value: number) => formatNumber(Number(value) || 0),
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      key: 'iva',
      width: 120,
      align: 'right',
      render: (value: number) => formatNumber(Number(value) || 0),
    },
    {
      title: 'Otros Imp.',
      dataIndex: 'otros_impuestos',
      key: 'otros_impuestos',
      width: 120,
      align: 'right',
      render: (value: number) => formatNumber(Number(value) || 0),
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (_, record) => formatNumber(calculateTotal(record)),
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 100,
      align: 'center',
      render: (value: number) => (
        <span style={{ color: value === 0 ? theme.colors.error : theme.colors.success }}>
          {value === 1 ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  return (
    <div ref={containerRef} className="page-transition-container" style={{ padding: '0' }}>
      <style>{`
        .page-transition-container {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          transform: translateX(0);
          opacity: 1;
        }
        .page-transition-container.slide-out-left {
          transform: translateX(-100%);
          opacity: 0;
        }
        .page-transition-container.slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Fletes
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Liquidaciones de Fletes"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleNewClick}
            size="small"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Nuevo
          </Button>
        }
      >
      <div style={{ padding: '24px' }}>
      {/* Filtros */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '24px',
        alignItems: 'end'
      }}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '14px'
          }}>
            Transportista:
          </label>
          <div className="transportista-search-container" style={{ position: 'relative', width: '100%' }}>
            <Input
              placeholder="Buscar por NC o denominación..."
              size="small"
              prefix={<SearchOutlined />}
              value={selectedTransportista ? `${selectedTransportista.nc} - ${selectedTransportista.denominacion}` : searchTransportista}
              onChange={e => {
                setSearchTransportista(e.target.value);
                setSelectedTransportista(null);
                setShowSearchTransportista(true);
              }}
              onFocus={() => setShowSearchTransportista(true)}
              allowClear={!!selectedTransportista}
              onClear={() => {
                setSelectedTransportista(null);
                setSearchTransportista('');
                setShowSearchTransportista(true);
              }}
              style={{
                backgroundColor: selectedTransportista ? theme.colors.backgroundGray : 'white',
                cursor: 'pointer'
              }}
            />
            {/* Dropdown de resultados */}
            {showSearchTransportista && transportistas.length > 0 && !selectedTransportista && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {transportistas.filter(t => {
                  const search = searchTransportista.trim().toLowerCase();
                  return (
                    t.nc.toLowerCase().includes(search) ||
                    t.denominacion.toLowerCase().includes(search)
                  );
                }).map(transportista => (
                  <div
                    key={transportista.id}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      textAlign: 'left'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      handleTransportistaChange(transportista);
                    }}
                  >
                    {transportista.nc} - {transportista.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div>
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            size="small"
            style={{
              backgroundColor: theme.colors.primary,
              borderColor: theme.colors.primary,
              fontWeight: 500,
              minWidth: '120px',
              borderRadius: theme.borderRadius.full
            }}
          >
            Buscar
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={liquidaciones}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300, y: 500 }}
        pagination={{
          position: ['bottomLeft'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
        }}
      />

      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: `1px solid ${theme.colors.borderLight}`
      }}>
        <Button 
          type="primary" 
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          disabled={!selectedRow || selectedRow.alta === 0}
          size="small"
          style={{
            fontWeight: 500,
            minWidth: '120px',
            opacity: (selectedRow && selectedRow.alta !== 0) ? 1 : 0.6
          }}
        >
          Imprimir
        </Button>
        <Button 
          danger
          icon={<StopOutlined />}
          onClick={handleCancel}
          disabled={!selectedRow || selectedRow.alta === 0}
          size="small"
          style={{
            fontWeight: 500,
            minWidth: '120px',
            opacity: (selectedRow && selectedRow.alta !== 0) ? 1 : 0.6
          }}
        >
          Anular
        </Button>
      </div>
      </div>
      </Card>
    </div>
  );
};

export default FletesLiquidaciones;
